const MAX_WIDTH = 1200;
const MAX_HEIGHT = 1200;

const DEFAULT_MIME = "image/jpeg";

/**
 * File を base64 に則った DataURL へ変換する
 */
const convertFileToDataUrlAsBase64 = async ({ file }: { file: File }): Promise<string> => {
	return new Promise((resolved, reject) => {
		const reader = new FileReader();
		reader.onload = (e) => {
			const dataUrl = e.target!.result as string;
			resolved(dataUrl);
		};
		reader.onerror = () => {
			reader.abort();
			reject(new DOMException("Problem parsing input file."));
		};

		reader.readAsDataURL(file);
	});
};

/**
 * Base64 画像のサイズを取得する
 */
const convertBase64ImageToImageSize = async ({ base64 }: { base64: string }): Promise<{ w: number; h: number }> => {
	return new Promise((resolved, _) => {
		const image = new Image();
		image.onload = () => {
			resolved({ w: image.width, h: image.height });
		};

		image.src = base64;
	});
};

/**
 * canvas を生成する
 */
const createCanvas = ({
	ratio,
	width,
	height,
}: {
	ratio: number;
	width: number;
	height: number;
}): HTMLCanvasElement => {
	if (ratio > 1) {
		// 縦長の画像
		width = height / ratio;
	} else {
		// 横長の画像
		height = width * ratio;
	}

	const canvas = document.createElement("canvas");

	canvas.width = width;
	canvas.height = height;

	return canvas;
};

/**
 * 画像の比率を計算する
 */
const calcImageRatio = ({ width, height }: { width: number; height: number }): number => {
	return height / width;
};

/**
 * 入力された File をリサイズした Base64 画像に変換する
 */
const convertFileToResizeImage = async (file: File): Promise<string> => {
	const image = new Image();

	image.src = await convertFileToDataUrlAsBase64({ file });

	const { h, w } = await convertBase64ImageToImageSize({ base64: image.src });

	const ratio = calcImageRatio({ width: w, height: h });

	const canvas = createCanvas({ ratio, width: MAX_WIDTH, height: MAX_HEIGHT });

	const ctx = canvas.getContext("2d")!;

	// PNG透過対応：透明背景を明示的に設定
	const isPNG = file.type === "image/png" || file.name.toLowerCase().endsWith(".png");
	if (isPNG) {
		// canvasを完全に透明にリセット
		ctx.clearRect(0, 0, canvas.width, canvas.height);
		// 合成モードを適切に設定
		ctx.globalCompositeOperation = "source-over";
		// 透明度を保持するためのalpha設定
		ctx.globalAlpha = 1.0;
	}

	ctx.drawImage(image, 0, 0, image.width, image.height, 0, 0, canvas.width, canvas.height);

	// PNG形式の場合は透過を保持、品質も指定
	const outputType = isPNG ? "image/png" : file.type || DEFAULT_MIME;
	const quality = isPNG ? undefined : 0.9; // PNGの場合はqualityパラメータを使わない
	return canvas.toDataURL(outputType, quality);
};

/**
 * Base64 画像から File を生成する
 */
const convertBase64ToFile = (base64: string, name: string, mime?: string): File => {
	const bin = atob(base64.replace(/^.*,/, ""));
	// バイナリデータ化
	const buffer = new Uint8Array(bin.length);
	for (let i = 0; i < bin.length; i++) {
		buffer[i] = bin.charCodeAt(i);
	}
	// ファイルオブジェクト生成(この例ではjpegファイル)
	return new File([buffer.buffer], name, { type: mime || DEFAULT_MIME });
};

/**
 * リサイズした画像を生成する
 */
export async function createResizeImageFile({ file }: { file: File }): Promise<{ resizedImageFile: File }> {
	if (!file.name.match(/^.*(\.)((png)|(jpg)|(jpeg)|(gif)|(PNG)|(JPG)|(JPEG)|(GIF)|(webp))$/)) {
		throw new Error();
	}

	const convertedResizeImage = await convertFileToResizeImage(file);
	const isPNG = file.type === "image/png" || file.name.toLowerCase().endsWith(".png");
	const convertedImageFile = convertBase64ToFile(convertedResizeImage, file.name, isPNG ? "image/png" : undefined);

	return {
		resizedImageFile: convertedImageFile,
	};
}
